import geoJSON from "geojson";
import {
  compact,
  filter,
  find,
  flatten,
  get,
  head,
  intersection,
  isEmpty,
  map as lodashMap,
  memoize,
  uniq,
  values
} from "lodash";
import {
  EventData,
  GeolocateControl,
  Map,
  MapboxEvent,
  MapMouseEvent,
  NavigationControl
} from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Feature, GeoJSONLayer, Layer, Marker, Source } from "react-mapbox-gl";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { MIN_ZOOM } from ".";
import { useAppearance } from "../../../context/appearance";
import { useContacts } from "../../../context/contacts";
import { useDiscordChannels } from "../../../context/discordChannels";
import { useFilteredList } from "../../../context/filteredList";
import { useHotspots } from "../../../context/hotspots";
import { useInstalls } from "../../../context/installs";
import { useLineOfSight } from "../../../context/lineOfSight";
import { useLocations } from "../../../context/locations";
import { usePlacements } from "../../../context/placements";
import { useToast } from "../../../context/toast";
import { useTooCloseToWitness } from "../../../context/tooCloseToWitness";
import { TooCloseToWitnessLineCoordinates } from "../../../context/tooCloseToWitness/TooCloseToWitnessProvider";
import { useTransmitScales } from "../../../context/transmitScales";
import { Entity } from "../../../utils/constants";
import {
  classificationColorMap,
  classificationRgbColorMap,
  getClassification,
  relativePerformanceClassifications
} from "../../../utils/hotspotUtils";
import {
  h3SetToMultiPolygonMemoized,
  h3ToChildrenMemoized,
  h3ToGeoMemoized,
  kRingMemoized,
  pointDistanceMemoized
} from "../../../utils/memoizedMethods";
import useMapCenter from "../../../utils/useMapCenter";
import useMapConfig, {
  invalidColor,
  validColor,
  VisualizationLayer,
  WitnessesVisualizationKey,
  WitnessesVisualizationVariant
} from "../../../utils/useMapConfig";
import usePrevious from "../../../utils/usePrevious";
import useTimeTravel from "../../../utils/useTimeTravel";
import { isActiveInstall } from "../../Workspace/InstallsData/utils";
import { getHexNeighborsWithLabel } from "../HexDetails/HexTransmitScaleTab/NeighborHexesTab";
import CbrsLayer from "./Layers/CbrsLayer";

interface Props {
  map: Map;
  containerStyle: React.CSSProperties;
  updateElevationContourColors: (map: Map) => void;
  isMoving: boolean;
}

const MapContent: React.FC<Props> = ({
  map,
  containerStyle,
  updateElevationContourColors,
  isMoving
}) => {
  const { addToast } = useToast();
  const {
    hotspotWitnessesDataMap,
    getHotspotCoordinates,
    currentHotspot,
    hotspotsMap,
    hotspotsAroundCenter,
    fetchWitnessHotspots
  } = useHotspots();
  const { channels } = useDiscordChannels();
  const { tooCloseToWitnessData } = useTooCloseToWitness();
  const { isDark } = useAppearance();
  const {
    calculatedTransmitScales,
    problematicHexes,
    getTransmitScaleInfo,
    getHexTransmitScaleInfo,
    isTransmitScaleActive,
    getLocationWitnessRewardUnits
  } = useTransmitScales();
  const { histogramBucketIndexMap, filteredList } = useFilteredList();
  const fetchedDataCenter = useMapCenter((state) => state.fetchedDataCenter);
  const [center, setCenter] = useMapCenter((state) => [
    state.center,
    state.setCenter
  ]);
  const setZoom = useMapCenter((state) => state.setZoom);
  const [flyingComplete, setFlyingComplete] = useState<boolean>(false);
  const { pathname, search } = useLocation();
  const history = useHistory();
  const explorerPageMatch = matchPath<{
    tab?: string; // hotspots, locations, hexes, contacts, installs
  }>(pathname, {
    path: "/:tab"
  });
  const detailsPageMatch = matchPath<{
    tab: string; // hotspots, locations, hexes, contacts, installs
    id: string;
    section: string;
    subsection?: string;
  }>(pathname, {
    path: ["/:tab/:id/:section/:subsection", "/:tab/:id/:section"]
  });

  const hotspotMakerDetailsPageMatch = matchPath<{
    id?: string;
    section?: string;
    subsection?: string;
  }>(pathname, {
    path: [
      "/hotspots/makers/:id/:section/:subsection",
      "/hotspots/makers/:id/:section"
    ]
  });

  const timeTravelPageMatch = matchPath(pathname, {
    path: "/hotspots/time-travel"
  });
  const timeTravelTimestamp = useTimeTravel((state) => state.timestamp);
  const witnessesVisualizationKey = useMapConfig(
    (state) => state.witnessesVisualizationKey
  );
  const witnessesVisualizationVariant = useMapConfig(
    (state) => state.witnessesVisualizationVariant
  );
  const visibleLayers = useMapConfig((state) => state.visibleLayers);
  const pointColorMetricIteratee = useMapConfig(
    (state) => state.pointColorMetricIteratee
  );
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const {
    locations,
    locationsAroundCenter,
    selectPointsOnMapConfig,
    getLocationCoordinates,
    simulatedLocations,
    currentSuperSimulation
  } = useLocations();
  const { contacts } = useContacts();
  const { installs } = useInstalls();
  const { placements } = usePlacements();
  const {
    startPoint,
    endPoint,
    lineOfSightLineColor,
    isEditingStart,
    isEditingEnd
  } = useLineOfSight();

  const filteredHotspotsWithLocation = useMemo(
    () =>
      filteredList[Entity.hotspot].filter(
        (hotspot) => hotspot.lat && hotspot.lng
      ),
    [filteredList]
  );

  const filteredLocations = useMemo(
    () => filteredList[Entity.location],
    [filteredList]
  );

  const currentLocation = useMemo(
    () =>
      detailsPageMatch?.params.id
        ? locations[detailsPageMatch.params.id]
        : undefined,

    [locations, detailsPageMatch?.params.id]
  );

  const currentContact = useMemo(
    () =>
      detailsPageMatch?.params.id
        ? contacts[detailsPageMatch.params.id]
        : undefined,
    [contacts, detailsPageMatch?.params.id]
  );

  const activeInstallsForCurrentContact = useMemo(
    () =>
      values(installs).filter(
        (install) =>
          !!currentContact &&
          (currentContact?.linked_location_ids.includes(
            install.location_uuid
          ) ||
            !!find(install.commissions, {
              contact_uuid: currentContact.uuid
            })) &&
          isActiveInstall(install)
      ),
    [currentContact, installs]
  );

  const filteredInstalls = useMemo(
    () => filteredList[Entity.install],
    [filteredList]
  );

  const currentInstall = useMemo(
    () =>
      detailsPageMatch?.params.id
        ? installs[detailsPageMatch.params.id]
        : undefined,
    [installs, detailsPageMatch?.params.id]
  );

  useEffect(() => {
    map.touchZoomRotate.disableRotation();
    map.touchPitch.disable();
    map.addControl(
      new GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true
      })
    );
    map.addControl(
      new NavigationControl({
        showCompass: false,
        showZoom: true
      })
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    map.resize();
  }, [containerStyle, map]);

  const filteredHexes = useMemo(() => filteredList[Entity.hex], [filteredList]);

  const filteredHexesToDisplay = useMemo(() => {
    if (
      !!timeTravelPageMatch ||
      (explorerPageMatch?.params.tab &&
        ["contacts", "installs"].includes(explorerPageMatch.params.tab))
    )
      return [];

    // Hex details page
    if (
      detailsPageMatch?.params.tab === "hexes" &&
      detailsPageMatch?.params.id
    ) {
      return intersection(
        kRingMemoized(detailsPageMatch.params.id, 1),
        problematicHexes
      );
    }

    // Hotspot & location details page in reward scaling tab
    const currentHotspotOrLocationH3Index =
      currentHotspot?.location || currentLocation?.h3_index_r12;
    if (currentHotspotOrLocationH3Index) {
      if (detailsPageMatch?.params.section === "reward-scaling") {
        const { parentHexes, problematicResolutions } = getTransmitScaleInfo(
          currentHotspotOrLocationH3Index
        );
        return intersection(
          problematicResolutions.map((resolution) => parentHexes[resolution]),
          problematicHexes
        );
      } else {
        return [];
      }
    }

    // Hexes explorer page
    if (explorerPageMatch?.params.tab === "hexes") {
      return lodashMap(filteredHexes, "h3Index");
    }

    // If problematic hexes are toggled in the settings
    if (
      visibleLayers[VisualizationLayer.problematicHexes] ||
      !!selectPointsOnMapConfig?.placementContext
    ) {
      return problematicHexes;
    }

    // Don't show problematic hexes otherwise
    return [];
  }, [
    timeTravelPageMatch,
    explorerPageMatch?.params.tab,
    detailsPageMatch?.params.tab,
    detailsPageMatch?.params.id,
    detailsPageMatch?.params.section,
    currentHotspot?.location,
    currentLocation?.h3_index_r12,
    visibleLayers,
    selectPointsOnMapConfig?.placementContext,
    problematicHexes,
    getTransmitScaleInfo,
    filteredHexes
  ]);

  const channelHexIndices = useMemo(
    () =>
      lodashMap(filter(values(channels), { type: Entity.hex }), "linked_id"),
    [channels]
  );

  const channelHexesToDisplay = useMemo(() => {
    if (
      explorerPageMatch?.params.tab !== "hexes" ||
      detailsPageMatch?.params.id
    ) {
      return [];
    }

    return intersection(channelHexIndices, lodashMap(filteredHexes, "h3Index"));
  }, [
    channelHexIndices,
    detailsPageMatch?.params.id,
    explorerPageMatch?.params.tab,
    filteredHexes
  ]);

  const filteredContacts = useMemo(
    () => filteredList[Entity.contact],
    [filteredList]
  );

  const locationsToDisplay = useMemo(() => {
    if (
      !!timeTravelPageMatch ||
      !!hotspotMakerDetailsPageMatch ||
      (!visibleLayers[VisualizationLayer.locations] &&
        explorerPageMatch?.params.tab !== "locations")
    ) {
      return [];
    }

    if (explorerPageMatch?.params.tab === "locations")
      return filteredLocations.filter(
        (location) =>
          selectPointsOnMapConfig?.placementContext?.location?.uuid !==
          location.uuid
      );

    if (!!currentContact) {
      const uniqueLinkedLocationIds = uniq(
        lodashMap(activeInstallsForCurrentContact, "location_uuid").concat(
          currentContact.linked_location_ids
        )
      );

      return compact(uniqueLinkedLocationIds.map((uuid) => locations[uuid]));
    }

    if (!!currentInstall) {
      return compact(
        [currentInstall.location_uuid].map((uuid) => locations[uuid])
      );
    }

    if (
      explorerPageMatch?.params.tab === "contacts" &&
      visibleLayers[VisualizationLayer.locations]
    ) {
      const linkedContactLocationUuids = uniq(
        flatten(lodashMap(filteredContacts, "linked_location_ids"))
      );

      return compact(linkedContactLocationUuids.map((uuid) => locations[uuid]));
    }

    if (
      explorerPageMatch?.params.tab === "installs" &&
      visibleLayers[VisualizationLayer.locations]
    ) {
      return compact(
        filteredInstalls.map(({ location_uuid }) => locations[location_uuid])
      );
    }

    if (explorerPageMatch?.params.tab === "locations") {
      return filteredLocations;
    }

    if (
      detailsPageMatch?.params.section === "reward-scaling" ||
      detailsPageMatch?.params.section === "transmit-scale" ||
      visibleLayers[VisualizationLayer.locations]
    ) {
      return locationsAroundCenter.concat(simulatedLocations);
    }

    return [];
  }, [
    timeTravelPageMatch,
    hotspotMakerDetailsPageMatch,
    explorerPageMatch?.params.tab,
    filteredLocations,
    currentContact,
    currentInstall,
    visibleLayers,
    detailsPageMatch?.params.section,
    selectPointsOnMapConfig?.placementContext?.location?.uuid,
    activeInstallsForCurrentContact,
    locations,
    filteredContacts,
    filteredInstalls,
    locationsAroundCenter,
    simulatedLocations
  ]);

  const superSimulationDetailsPageMatch = matchPath<{
    locationUuid: string;
    simulationUuid: string;
    subsection: string;
  }>(pathname, {
    path: "/locations/:locationUuid/super-simulations/:simulationUuid/:subsection"
  });

  const showSuperSimulation = useMemo(() => {
    if (!currentLocation) return false;

    if (!superSimulationDetailsPageMatch?.params.simulationUuid) return false;

    if (!currentSuperSimulation) return false;

    return (
      currentLocation.lat === currentSuperSimulation.simulation_report?.lat &&
      currentLocation.lng === currentSuperSimulation.simulation_report?.lng
    );
  }, [
    currentLocation,
    currentSuperSimulation,
    superSimulationDetailsPageMatch?.params.simulationUuid
  ]);

  const witnessHotspotIds = useMemo(() => {
    if (currentHotspot) {
      return lodashMap(
        get(
          hotspotWitnessesDataMap,
          [
            currentHotspot?.id,
            witnessesVisualizationKey,
            witnessesVisualizationVariant
          ],
          []
        ) as any,
        "id"
      );
    }

    if (showSuperSimulation) {
      return currentSuperSimulation?.simulation_report?.vh || [];
    }

    return [];
  }, [
    currentHotspot,
    currentSuperSimulation?.simulation_report?.vh,
    hotspotWitnessesDataMap,
    showSuperSimulation,
    witnessesVisualizationKey,
    witnessesVisualizationVariant
  ]);

  useEffect(() => {
    if (!isEmpty(witnessHotspotIds)) {
      fetchWitnessHotspots(witnessHotspotIds);
    }
  }, [fetchWitnessHotspots, witnessHotspotIds]);

  const filteredHotspotsIncludingWitnesses = useMemo(() => {
    const results = filteredHotspotsWithLocation;

    for (const hotspotId of witnessHotspotIds) {
      const hotspot = hotspotsMap[hotspotId];
      if (
        hotspot &&
        hotspot.lat &&
        hotspot.lng &&
        !results.find((hotspot) => hotspot.id === hotspotId)
      ) {
        results.push(hotspot);
      }
    }

    return results;
  }, [filteredHotspotsWithLocation, hotspotsMap, witnessHotspotIds]);

  const hotspotsToDisplay = useMemo(() => {
    if (
      showSuperSimulation ||
      detailsPageMatch?.params.section === "witnesses"
    ) {
      return filteredHotspotsIncludingWitnesses;
    }

    if (
      !visibleLayers[VisualizationLayer.hotspots] &&
      explorerPageMatch?.params.tab !== "hotspots"
    ) {
      return [];
    }

    if (currentContact) {
      return compact(
        activeInstallsForCurrentContact.map(
          ({ hotspot_uuid }) => hotspotsMap[hotspot_uuid]
        )
      );
    }

    if (
      explorerPageMatch?.params.tab === "contacts" &&
      visibleLayers[VisualizationLayer.hotspots]
    ) {
      const filteredContactsLocationUuids = flatten(
        uniq(lodashMap(filteredContacts, "linked_location_ids"))
      );

      const activeInstallsForFilteredContacts = values(installs).filter(
        (install) =>
          filteredContactsLocationUuids.includes(install.location_uuid) &&
          isActiveInstall(install)
      );

      return compact(
        activeInstallsForFilteredContacts.map(
          ({ hotspot_uuid }) => hotspotsMap[hotspot_uuid]
        )
      );
    }

    if (currentInstall) {
      return compact([hotspotsMap[currentInstall.hotspot_uuid]]);
    }

    if (
      explorerPageMatch?.params.tab === "installs" &&
      visibleLayers[VisualizationLayer.hotspots]
    ) {
      return compact(
        uniq(lodashMap(filteredInstalls, "hotspot_uuid")).map(
          (hotspotId) => hotspotsMap[hotspotId]
        )
      );
    }

    if (hotspotMakerDetailsPageMatch?.params.id) {
      return filter(filteredHotspotsWithLocation, {
        payer: hotspotMakerDetailsPageMatch.params.id
      });
    }

    if (explorerPageMatch?.params.tab === "hotspots") {
      return filteredHotspotsWithLocation;
    }

    if (
      detailsPageMatch?.params.section === "reward-scaling" ||
      detailsPageMatch?.params.section === "transmit-scale" ||
      visibleLayers[VisualizationLayer.hotspots]
    ) {
      return hotspotsAroundCenter.filter(
        (hotspot) =>
          selectPointsOnMapConfig?.placementContext?.hotspot?.id !== hotspot.id
      );
    }

    return [];
  }, [
    showSuperSimulation,
    visibleLayers,
    explorerPageMatch?.params.tab,
    currentContact,
    currentInstall,
    hotspotMakerDetailsPageMatch?.params.id,
    detailsPageMatch?.params.section,
    filteredHotspotsIncludingWitnesses,
    activeInstallsForCurrentContact,
    hotspotsMap,
    filteredContacts,
    installs,
    filteredInstalls,
    filteredHotspotsWithLocation,
    hotspotsAroundCenter,
    selectPointsOnMapConfig?.placementContext?.hotspot?.id
  ]);

  const selectedItemCoordinates = useMemo((): [number, number] | undefined => {
    let coordinates: { h3Index: string; lat: number; lng: number } | undefined;

    if (currentHotspot) {
      coordinates = getHotspotCoordinates(currentHotspot.id);
    } else if (currentLocation) {
      coordinates = getLocationCoordinates(currentLocation.uuid);
    }

    const lat = coordinates?.lat;
    const lng = coordinates?.lng;

    if (!!lng && !!lat) {
      return [lng, lat];
    }

    return;
  }, [
    currentHotspot,
    currentLocation,
    getHotspotCoordinates,
    getLocationCoordinates
  ]);

  const itemIdsToConnect: string[] = useMemo(() => {
    const selectedItemId = detailsPageMatch?.params.id;

    if (!selectedItemId || !visibleLayers[VisualizationLayer.witnessLines]) {
      return [];
    }

    if (showSuperSimulation) {
      return (currentSuperSimulation?.simulation_report?.vh || []).concat(
        currentSuperSimulation?.simulation_report?.vl || []
      );
    }

    if (hotspotWitnessesDataMap) {
      switch (witnessesVisualizationKey) {
        case WitnessesVisualizationKey.witnessedBy:
        case WitnessesVisualizationKey.witnessedOthers:
        case WitnessesVisualizationKey.bidirectional:
          return lodashMap(
            get(
              hotspotWitnessesDataMap,
              [
                selectedItemId,
                witnessesVisualizationKey,
                witnessesVisualizationVariant
              ],
              []
            ) as any,
            "id"
          );
        case WitnessesVisualizationKey.none:
        default:
          return [];
      }
    }

    return [];
  }, [
    currentSuperSimulation?.simulation_report?.vh,
    currentSuperSimulation?.simulation_report?.vl,
    detailsPageMatch?.params.id,
    hotspotWitnessesDataMap,
    showSuperSimulation,
    visibleLayers,
    witnessesVisualizationKey,
    witnessesVisualizationVariant
  ]);

  const getWitnessColor = useCallback(
    (id: string) => {
      let superSimulationVisibleLocationUuids: string[] = [];
      if (showSuperSimulation) {
        superSimulationVisibleLocationUuids =
          currentSuperSimulation?.simulation_report?.vl || [];
      }

      const isWitness = witnessHotspotIds
        .concat(superSimulationVisibleLocationUuids)
        .includes(id);

      const getHotspotTransmitScale = (hotspotId: string) =>
        isTransmitScaleActive(id)
          ? calculatedTransmitScales[hotspotId] ||
            get(hotspotsMap, [hotspotId, "reward_scale"]) ||
            0
          : 0;

      const selectedHotspotTransmitScale = currentHotspot
        ? getHotspotTransmitScale(currentHotspot.id)
        : 0;

      const selectedHotspotTransmitScaleColor =
        classificationColorMap[getClassification(selectedHotspotTransmitScale)];

      const connectedItemTransmitScale = getHotspotTransmitScale(id);

      const connectedHotspotTransmitScaleColor =
        classificationColorMap[getClassification(connectedItemTransmitScale)];

      if (showSuperSimulation) {
        let connectedHotspotWitnessScore;

        const connectedHotspot = hotspotsMap[id];
        const connectedLocation = locations[id];

        if (connectedHotspot) {
          connectedHotspotWitnessScore = connectedHotspot.witness_score;
        } else if (connectedLocation) {
          const witnessRewardUnits =
            getLocationWitnessRewardUnits(connectedLocation);
          const transmitScale = calculatedTransmitScales[id];

          if (witnessRewardUnits && transmitScale) {
            connectedHotspotWitnessScore = witnessRewardUnits * transmitScale;
          }
        }

        if (isWitness && connectedHotspotWitnessScore) {
          return classificationColorMap[
            getClassification(connectedHotspotWitnessScore)
          ];
        }

        return !!connectedLocation
          ? "rgb(156, 163, 175)"
          : "rgba(156, 163, 175, 0.5)";
      }

      const currentHotspotId = currentHotspot?.id;

      if (!isWitness || !currentHotspotId) return "rgba(156, 163, 175, 0.5)";

      switch (witnessesVisualizationKey) {
        case WitnessesVisualizationKey.witnessedBy: {
          switch (witnessesVisualizationVariant) {
            case WitnessesVisualizationVariant.valid:
              return selectedHotspotTransmitScaleColor;
            case WitnessesVisualizationVariant.invalid:
              return invalidColor;
            case WitnessesVisualizationVariant.all:
            default: {
              const invalidWitnessedByData = get(
                hotspotWitnessesDataMap,
                [
                  currentHotspotId,
                  WitnessesVisualizationKey.witnessedBy,
                  WitnessesVisualizationVariant.invalid
                ],
                []
              );
              const invalidWitnessData = find(invalidWitnessedByData, {
                id
              });
              return !!invalidWitnessData ? invalidColor : validColor;
            }
          }
        }
        case WitnessesVisualizationKey.witnessedOthers: {
          switch (witnessesVisualizationVariant) {
            case WitnessesVisualizationVariant.valid:
              return connectedHotspotTransmitScaleColor;
            case WitnessesVisualizationVariant.invalid:
              return invalidColor;
            case WitnessesVisualizationVariant.all:
            default: {
              const invalidWitnessedOthersData = get(
                hotspotWitnessesDataMap,
                [
                  currentHotspotId,
                  WitnessesVisualizationKey.witnessedOthers,
                  WitnessesVisualizationVariant.invalid
                ],
                []
              );
              const invalidWitnessData = find(invalidWitnessedOthersData, {
                id
              });

              return !!invalidWitnessData ? invalidColor : validColor;
            }
          }
        }
        case WitnessesVisualizationKey.bidirectional:
          switch (witnessesVisualizationVariant) {
            case WitnessesVisualizationVariant.valid:
              return classificationColorMap[
                getClassification(
                  Math.min(
                    selectedHotspotTransmitScale,
                    connectedItemTransmitScale
                  )
                )
              ];
            case WitnessesVisualizationVariant.invalid:
              return invalidColor;
            case WitnessesVisualizationVariant.all:
            default: {
              const invalidBidirectionalData = get(
                hotspotWitnessesDataMap,
                [
                  currentHotspotId,
                  WitnessesVisualizationKey.bidirectional,
                  WitnessesVisualizationVariant.invalid
                ],
                []
              );
              const invalidWitnessData = find(invalidBidirectionalData, {
                id
              });
              return !!invalidWitnessData ? invalidColor : validColor;
            }
          }
        case WitnessesVisualizationKey.none:
        default:
          return "rgba(23, 48, 90, 0.5)";
      }
    },
    [
      showSuperSimulation,
      witnessHotspotIds,
      currentHotspot,
      witnessesVisualizationKey,
      currentSuperSimulation?.simulation_report?.vl,
      isTransmitScaleActive,
      calculatedTransmitScales,
      hotspotsMap,
      locations,
      getLocationWitnessRewardUnits,
      witnessesVisualizationVariant,
      hotspotWitnessesDataMap
    ]
  );

  useEffect(() => {
    let flying = false;

    if (!selectedItemCoordinates || !flyingComplete) return;

    if (flying) {
      map.once(
        "moveend",
        (
          event: MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined> &
            EventData
        ) => {
          flying = false;
          setFlyingComplete(true);
          setZoom((event.target as any).transform.tileZoom);
          setCenter(selectedItemCoordinates);
        }
      );
    } else {
      map.flyTo({
        center: selectedItemCoordinates,
        zoom: 14
      });
      map.fire("flystart");
      flying = true;
    }
  }, [flyingComplete, map, selectedItemCoordinates, setCenter, setZoom]);

  const tooCloseToWitnessLinesToDisplay = useMemo(() => {
    const lineCoordinates: [[number, number], [number, number]][] = [];

    if (
      !!timeTravelPageMatch ||
      explorerPageMatch?.params.tab === "contacts" ||
      explorerPageMatch?.params.tab === "installs" ||
      explorerPageMatch?.params.tab === "hexes" ||
      !visibleLayers[VisualizationLayer.tooCloseToWitness]
    ) {
      return lineCoordinates;
    }

    const hotspotCoordinates = compact(
      hotspotsToDisplay.map((hotspot) => getHotspotCoordinates(hotspot.id))
    );

    const locationCoordinates = compact(
      locationsToDisplay.map((location) =>
        getLocationCoordinates(location.uuid)
      )
    );

    const combinedPointsToDisplay =
      hotspotCoordinates.concat(locationCoordinates);

    get(tooCloseToWitnessData, "coordinates", []).forEach(
      ({ laA, loA, IA, laB, loB, IB }: TooCloseToWitnessLineCoordinates) => {
        if (
          find(combinedPointsToDisplay, { h3Index: IA }) &&
          find(combinedPointsToDisplay, { h3Index: IB })
        ) {
          lineCoordinates.push([
            [loA, laA],
            [loB, laB]
          ]);
        }
      }
    );

    return lineCoordinates;
  }, [
    explorerPageMatch?.params.tab,
    getHotspotCoordinates,
    getLocationCoordinates,
    hotspotsToDisplay,
    locationsToDisplay,
    timeTravelPageMatch,
    tooCloseToWitnessData,
    visibleLayers
  ]);

  const installLinesToDisplay = useMemo(() => {
    const lineCoordinates: [[number, number], [number, number]][] = [];

    if (
      !["installs", "contacts"].includes(
        // TODO: double check
        explorerPageMatch?.params.tab as string
      )
    ) {
      return lineCoordinates;
    }

    values(installs)
      .filter(isActiveInstall)
      .forEach((install) => {
        const location = find(locationsToDisplay, {
          uuid: install.location_uuid
        });
        const hotspot = find(hotspotsToDisplay, { id: install.hotspot_uuid });

        if (
          !location?.lat ||
          !location?.lng ||
          !hotspot?.lat ||
          !hotspot?.lng
        ) {
          return;
        }

        lineCoordinates.push([
          [location.lng, location.lat],
          [hotspot.lng, hotspot.lat]
        ]);
      });

    return lineCoordinates;
  }, [
    explorerPageMatch?.params.tab,
    hotspotsToDisplay,
    installs,
    locationsToDisplay
  ]);

  const lineOfSightLinesToDisplay = useMemo(() => {
    const lineCoordinates: [[number, number], [number, number]][] = [];

    if (isEditingEnd && startPoint) {
      lineCoordinates.push([startPoint, center]);
    } else if (isEditingStart && endPoint) {
      lineCoordinates.push([center, endPoint]);
    } else if (startPoint && endPoint) {
      lineCoordinates.push([startPoint, endPoint]);
    } else if (startPoint && !endPoint) {
      lineCoordinates.push([startPoint, center]);
    }

    return lineCoordinates;
  }, [center, endPoint, isEditingEnd, isEditingStart, startPoint]);

  const placementLinesToDisplay = useMemo(() => {
    const lineCoordinates: [[number, number], [number, number]][] = [];
    let pointDetails = { lng: 0, lat: 0 };

    if (
      !isMoving &&
      explorerPageMatch?.params.tab === "locations" &&
      (selectPointsOnMapConfig?.placementContext?.hotspot ||
        selectPointsOnMapConfig?.placementContext?.location)
    ) {
      if (selectPointsOnMapConfig.placementContext.hotspot) {
        const { lng, lat } = selectPointsOnMapConfig.placementContext.hotspot;

        pointDetails = { lng, lat };
      }

      if (selectPointsOnMapConfig.placementContext.location) {
        const { lng, lat } = selectPointsOnMapConfig.placementContext.location;

        pointDetails = { lng, lat };
      }
    }

    if (pointDetails.lng && pointDetails.lat) {
      lineCoordinates.push([[pointDetails.lng, pointDetails.lat], center]);
    }

    // TODO: use placements for too close to witness lines
    // TODO: render parent hexes when dropping a pin for a placement
    // TODO: render '300m' boundary around placement

    values(placements)
      .filter(
        ({ uuid }) =>
          selectPointsOnMapConfig?.placementContext?.placement?.uuid !== uuid
      )
      .forEach(({ placementable_id, placementable_type, lat, lng }) => {
        // TODO: add currently edited location (center)

        if (placementable_type === Entity.location) {
          const location = find(locationsToDisplay, {
            uuid: placementable_id
          });

          if (location?.lat && location?.lng) {
            lineCoordinates.push([
              [location.lng, location.lat],
              [lng, lat]
            ]);
          }
        }

        if (placementable_type === Entity.hotspot) {
          const hotspot = find(hotspotsToDisplay, { id: placementable_id });

          if (hotspot?.lat && hotspot?.lng) {
            lineCoordinates.push([
              [hotspot.lng, hotspot.lat],
              [lng, lat]
            ]);
          }
        }
      });

    return lineCoordinates;
  }, [
    center,
    explorerPageMatch?.params.tab,
    hotspotsToDisplay,
    isMoving,
    locationsToDisplay,
    placements,
    selectPointsOnMapConfig?.placementContext?.hotspot,
    selectPointsOnMapConfig?.placementContext?.location,
    selectPointsOnMapConfig?.placementContext?.placement?.uuid
  ]);

  const getItemColorRgba = useCallback(
    (id: string, metric: string) => {
      let classification;

      if (metric === "reward_scale") {
        classification = isTransmitScaleActive(id)
          ? getClassification(get(calculatedTransmitScales, id, 0))
          : "disabled";
      } else {
        const bucketIndex = get(histogramBucketIndexMap, [id, metric], 0);
        classification = relativePerformanceClassifications[bucketIndex];
      }

      return [
        ...classificationRgbColorMap[classification],
        classification === "disabled" ? 0.5 : 1
      ];
    },
    [calculatedTransmitScales, isTransmitScaleActive, histogramBucketIndexMap]
  );

  const selectedItemMarkerStyle = useMemo((): React.CSSProperties => {
    if (!detailsPageMatch?.params.id) return {};

    const rgba = getItemColorRgba(
      detailsPageMatch.params.id,
      currentLocation ? "reward_scale" : pointColorMetricIteratee
    );

    return {
      width: 14,
      height: 14,
      borderRadius: "50%",
      backgroundColor: `rgb(${rgba[0]}, ${rgba[1]}, ${rgba[2]})`,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      boxShadow: `0px 0px 0px 20px rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]},0.15), 0px 0px 0px 10px rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]},0.15)`,
      cursor: "pointer",
      pointerEvents: "none"
    };
  }, [
    currentLocation,
    detailsPageMatch?.params.id,
    getItemColorRgba,
    pointColorMetricIteratee
  ]);

  const showElevationContours = useMemo(
    () =>
      showSuperSimulation ||
      (explorerPageMatch?.params.tab &&
        ["hotspots", "locations"].includes(explorerPageMatch.params.tab) &&
        visibleLayers[VisualizationLayer.elevationContours]),
    [explorerPageMatch?.params.tab, showSuperSimulation, visibleLayers]
  );

  const prevShowElevationContours = usePrevious(showElevationContours);

  useEffect(() => {
    if (showElevationContours && !prevShowElevationContours) {
      map.once("idle", () => {
        updateElevationContourColors(map);
      });
    }
  }, [
    map,
    prevShowElevationContours,
    showElevationContours,
    updateElevationContourColors
  ]);

  const memoHotspotsToDisplay = useMemo(
    () =>
      hotspotsToDisplay.map((hotspot) => {
        const coordinates = getHotspotCoordinates(
          hotspot.id,
          (!!timeTravelPageMatch && timeTravelTimestamp) || null
        );

        if (!coordinates) return null;

        let color;

        if (!!timeTravelPageMatch) {
          color = "#a96ceb";
        } else if (
          detailsPageMatch?.params.tab === "hotspots" &&
          detailsPageMatch?.params.section === "witnesses" &&
          currentHotspot &&
          currentHotspot.id !== hotspot.id &&
          [
            WitnessesVisualizationKey.witnessedBy,
            WitnessesVisualizationKey.witnessedOthers,
            WitnessesVisualizationKey.bidirectional
          ].includes(witnessesVisualizationKey)
        ) {
          color = getWitnessColor(hotspot.id);
        } else if (showSuperSimulation) {
          color = getWitnessColor(hotspot.id);
        } else {
          const rgba = getItemColorRgba(hotspot.id, pointColorMetricIteratee);
          color = `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]},${rgba[3]})`;
        }

        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [coordinates.lng, coordinates.lat]
          },
          properties: {
            ...hotspot,
            color
          }
        };
      }),
    [
      currentHotspot,
      detailsPageMatch?.params.section,
      detailsPageMatch?.params.tab,
      getHotspotCoordinates,
      getItemColorRgba,
      getWitnessColor,
      hotspotsToDisplay,
      pointColorMetricIteratee,
      showSuperSimulation,
      timeTravelPageMatch,
      timeTravelTimestamp,
      witnessesVisualizationKey
    ]
  );

  const memoLocationsToDisplay = useMemo(
    () =>
      locationsToDisplay.map((location) => {
        const coordinates = getLocationCoordinates(location.uuid);

        if (!coordinates) return null;

        let color;

        if (showSuperSimulation) {
          color = getWitnessColor(location.uuid);
        } else {
          const rgba = getItemColorRgba(location.uuid, "reward_scale");
          color = `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]},${rgba[3]})`;
        }

        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [coordinates.lng, coordinates.lat]
          },
          properties: {
            ...location,
            color
          }
        };
      }),
    [
      getItemColorRgba,
      getLocationCoordinates,
      getWitnessColor,
      locationsToDisplay,
      showSuperSimulation
    ]
  );

  return (
    <>
      <Source
        id="mapbox-terrain"
        tileJsonSource={{
          type: "vector",
          url: "mapbox://mapbox.mapbox-terrain-v2",
          attribution: "Mapbox Terrain tileset v2"
        }}
        onSourceLoaded={() => {
          map.once("idle", () => {
            updateElevationContourColors(map);
          });
        }}
      />
      <Layer
        id="contour"
        type="line"
        sourceId="mapbox-terrain"
        sourceLayer="contour"
        layout={{ visibility: showElevationContours ? "visible" : "none" }}
        paint={{
          "line-opacity": isDark ? 0.5 : 1,
          "line-color": "transparent"
        }}
      />
      <Layer
        id="filled-contours"
        type="fill"
        sourceId="mapbox-terrain"
        sourceLayer="contour"
        layout={{ visibility: showElevationContours ? "visible" : "none" }}
        paint={{
          "fill-opacity": isDark ? 0.05 : 0.1,
          "fill-color": "transparent",
          "fill-outline-color": "transparent"
        }}
      />
      {showElevationContours && <div className="elevation-profile-legend" />}
      <GeoJSONLayer
        id="too-close-to-witness"
        data={
          isEmpty(tooCloseToWitnessLinesToDisplay)
            ? emptyGeoJSON
            : {
                type: "FeatureCollection",
                features: tooCloseToWitnessLinesToDisplay.map(
                  (coordinates) => ({
                    type: "Feature",
                    geometry: {
                      type: "LineString",
                      coordinates
                    }
                  })
                )
              }
        }
        linePaint={tooCloseToWitnessLinePaint}
        lineLayout={tooCloseToWitnessLineLayout}
      />
      <GeoJSONLayer
        id="install-lines"
        data={
          isEmpty(installLinesToDisplay)
            ? emptyGeoJSON
            : {
                type: "FeatureCollection",
                features: installLinesToDisplay.map((coordinates) => ({
                  type: "Feature",
                  geometry: {
                    type: "LineString",
                    coordinates
                  }
                }))
              }
        }
        linePaint={installLinePaint}
        lineLayout={installLineLayout}
      />
      <GeoJSONLayer
        id="placement-lines"
        data={
          isEmpty(placementLinesToDisplay)
            ? emptyGeoJSON
            : {
                type: "FeatureCollection",
                features: placementLinesToDisplay.map((coordinates) => {
                  const pointDistance = pointDistanceMemoized(...coordinates);
                  return {
                    type: "Feature",
                    geometry: {
                      type: "LineString",
                      coordinates
                    },
                    properties: {
                      distance: renderDistance(
                        pointDistance,
                        pointDistance > 1000
                      )
                    }
                  };
                })
              }
        }
        linePaint={installLinePaint}
        lineLayout={installLineLayout}
        symbolLayout={placementLineSymbolLayout}
        symbolPaint={{
          "text-color": isDark ? "white" : "black"
        }}
      />

      <GeoJSONLayer
        id="transmit-scale-hexes"
        data={
          isEmpty(filteredHexesToDisplay)
            ? emptyGeoJSON
            : {
                type: "FeatureCollection",
                features: filteredHexesToDisplay.map((h3Index) => ({
                  ...h3HighFidelityToGeoJSON(h3Index),
                  properties: {
                    h3Index,
                    transmitScale:
                      getHexTransmitScaleInfo(h3Index).transmitScale === 1
                        ? "transparent"
                        : classificationColorMap[
                            getClassification(
                              getHexTransmitScaleInfo(h3Index).transmitScale
                            )
                          ]
                  }
                }))
              }
        }
        fillPaint={{
          "fill-color": ["get", "transmitScale"],
          "fill-opacity": 0.3
        }}
        fillOnClick={(e: MapMouseEvent) => {
          const featuresAtThisPoint = map.queryRenderedFeatures(e.point);

          const wasTheClickOnATransmitScaleHex = featuresAtThisPoint.every(
            (feature) => feature.source === "transmit-scale-hexes"
          );

          if (!wasTheClickOnATransmitScaleHex) return;

          const features = map.queryRenderedFeatures(e.point, {
            layers: ["transmit-scale-hexes-fill"]
          });

          const h3Index = get(head(features), "properties.h3Index");

          if (h3Index) {
            history.push(`/hexes/${h3Index}/transmit-scale`);
          }

          map.getCanvas().style.cursor = "";
        }}
        fillOnMouseEnter={(e: MapMouseEvent) => {
          map.getCanvas().style.cursor = "pointer";
        }}
        fillOnMouseLeave={(e: MapMouseEvent) => {
          map.getCanvas().style.cursor = "";
        }}
      />
      {channelHexesToDisplay.map((h3Index) => (
        <Layer
          key={`hex-channel-${h3Index}`}
          type="line"
          layout={currentHexRingLayout}
          paint={{
            "line-width": 2,
            "line-color": "#3b82f6"
          }}
        >
          <Feature
            coordinates={
              head(
                head(
                  h3SetToMultiPolygonMemoized(
                    h3ToChildrenMemoized(h3Index, 10),
                    true
                  )
                )
              ) as any
            }
          />
        </Layer>
      ))}
      {detailsPageMatch?.params.tab === "hexes" && detailsPageMatch?.params.id && (
        <Layer
          type="line"
          layout={currentHexRingLayout}
          paint={{
            "line-width": 2,
            "line-color": channelHexIndices.includes(detailsPageMatch.params.id)
              ? "#3b82f6"
              : isDark
              ? "white"
              : "black"
          }}
        >
          <Feature
            coordinates={
              head(
                head(
                  h3SetToMultiPolygonMemoized(
                    h3ToChildrenMemoized(detailsPageMatch.params.id, 10),
                    true
                  )
                )
              ) as any
            }
          />
        </Layer>
      )}
      {detailsPageMatch?.params.tab === "hexes" && detailsPageMatch?.params.id && (
        <>
          <GeoJSONLayer
            id="neighbor-hexes"
            data={
              isEmpty(getHexNeighborsWithLabel(detailsPageMatch.params.id))
                ? emptyGeoJSON
                : {
                    type: "FeatureCollection",
                    features: flatten(
                      getHexNeighborsWithLabel(detailsPageMatch.params.id).map(
                        ([neighborH3Index, letter]) => {
                          const [latitude, longitude] =
                            h3ToGeoMemoized(neighborH3Index);
                          return [
                            {
                              ...h3HighFidelityToGeoJSON(neighborH3Index),
                              properties: {
                                h3Index: neighborH3Index,
                                fillColor: problematicHexes.includes(
                                  neighborH3Index
                                )
                                  ? "transparent"
                                  : "rgba(249, 250, 251, 0.2)"
                              }
                            },
                            {
                              type: "Feature",
                              geometry: {
                                type: "Point",
                                coordinates: [longitude, latitude]
                              },
                              properties: {
                                letter
                              }
                            }
                          ];
                        }
                      )
                    )
                  }
            }
            symbolLayout={neighborHexLabelSymbolLayout}
            symbolPaint={{
              "text-color": isDark ? "white" : "black"
            }}
            fillPaint={{
              "fill-color": ["get", "fillColor"],
              "fill-opacity": 0.6
            }}
            fillOnClick={(e: MapMouseEvent) => {
              const selectedLocationUuid = head(
                map
                  .queryRenderedFeatures(e.point, {
                    layers: ["locations-circle"]
                  })
                  .map(mapFeatureToItem)
              )?.uuid;

              const selectedHotspotId = head(
                map
                  .queryRenderedFeatures(e.point, {
                    layers: ["hotspots-circle"]
                  })
                  .map(mapFeatureToItem)
              )?.id;

              const h3Index = get(
                head(
                  map.queryRenderedFeatures(e.point, {
                    layers: ["neighbor-hexes-fill"]
                  })
                ),
                "properties.h3Index"
              );

              if (selectedLocationUuid || selectedHotspotId || !h3Index) {
                map.getCanvas().style.cursor = "";
                return;
              }

              const {
                section,
                subsection
              }: {
                id?: string;
                section?: string;
                subsection?: string;
              } = detailsPageMatch?.params || {};

              let sectionPath = "transmit-scale";
              if (section) {
                sectionPath = subsection ? `${section}/${subsection}` : section;
              }

              history.push(`/hexes/${h3Index}/${sectionPath}${search}`);

              map.getCanvas().style.cursor = "";
            }}
            fillOnMouseEnter={(e: MapMouseEvent) => {
              map.getCanvas().style.cursor = "pointer";
            }}
            fillOnMouseLeave={(e: MapMouseEvent) => {
              map.getCanvas().style.cursor = "";
            }}
          />
          {getHexNeighborsWithLabel(detailsPageMatch.params.id).map(
            ([neighborH3Index]) => (
              <Layer
                key={neighborH3Index}
                type="line"
                layout={currentHexRingLayout}
                paint={neighborHexRingPaint}
              >
                <Feature
                  coordinates={
                    head(
                      head(
                        h3SetToMultiPolygonMemoized(
                          h3ToChildrenMemoized(neighborH3Index, 10),
                          true
                        )
                      )
                    ) as any
                  }
                />
              </Layer>
            )
          )}
        </>
      )}
      {fetchedDataCenter && (
        <Layer
          type="line"
          layout={fetchedDataBoundaryLayout}
          paint={fetchedDataBoundaryPaint}
          minZoom={MIN_ZOOM}
        >
          <Feature
            coordinates={
              head(
                head(
                  h3SetToMultiPolygonMemoized(
                    kRingMemoized(fetchedDataCenter, 2),
                    true
                  )
                )
              ) as any
            }
          />
        </Layer>
      )}

      {selectedItemCoordinates &&
        (detailsPageMatch?.params.section === "witnesses" ||
          detailsPageMatch?.params.tab === "locations") &&
        itemIdsToConnect.map((connectedItemId) => {
          const connectedItem =
            hotspotsMap[connectedItemId] || locations[connectedItemId];

          if (!connectedItem) return null;

          return (
            <Layer
              key={connectedItemId}
              type="line"
              layout={{
                "line-cap": "round",
                "line-join": "round"
              }}
              paint={{
                "line-color": getWitnessColor(connectedItemId),
                "line-width": 2,
                "line-opacity": 0.5
              }}
            >
              <Feature
                coordinates={[
                  selectedItemCoordinates,
                  [connectedItem.lng as number, connectedItem.lat as number]
                ]}
              />
            </Layer>
          );
        })}

      {selectedItemCoordinates && (
        <Marker
          key={detailsPageMatch?.params.id as string}
          style={selectedItemMarkerStyle}
          anchor="center"
          coordinates={selectedItemCoordinates}
          className="activeMarker"
        />
      )}
      <GeoJSONLayer
        id="locations"
        data={
          isEmpty(locationsToDisplay)
            ? emptyGeoJSON
            : {
                type: "FeatureCollection",
                features: compact(memoLocationsToDisplay)
              }
        }
        circlePaint={locationCirclePaint}
        circleOnClick={(e: MapMouseEvent) => {
          const selectedLocationUuid = head(
            map
              .queryRenderedFeatures(e.point, {
                layers: ["locations-circle"]
              })
              .map(mapFeatureToItem)
          )?.uuid;

          if (!selectedLocationUuid) {
            map.getCanvas().style.cursor = "";
            return;
          }

          if (find(simulatedLocations, { uuid: selectedLocationUuid })) {
            addToast({
              type: "info",
              title: "Simulated location",
              message:
                "You clicked on a simulated location, which is temporary and doesn't have a details page."
            });
          } else if (locations[selectedLocationUuid]) {
            history.push(
              `/locations/${selectedLocationUuid}/${
                detailsPageMatch?.params?.section || "super-simulations"
              }${search}`
            );
          }

          map.getCanvas().style.cursor = "";
        }}
        circleOnMouseEnter={(e: MapMouseEvent) => {
          map.getCanvas().style.cursor = "pointer";
        }}
        circleOnMouseLeave={(e: MapMouseEvent) => {
          map.getCanvas().style.cursor = "";
        }}
      />
      <GeoJSONLayer
        id="hotspots"
        data={
          isEmpty(hotspotsToDisplay)
            ? emptyGeoJSON
            : {
                type: "FeatureCollection",
                features: compact(memoHotspotsToDisplay)
              }
        }
        circlePaint={hotspotCirclePaint}
        circleOnClick={(e: MapMouseEvent) => {
          const selectedHotspotId = head(
            map
              .queryRenderedFeatures(e.point, {
                layers: ["hotspots-circle"]
              })
              .map(mapFeatureToItem)
          )?.id;

          if (!selectedHotspotId) {
            map.getCanvas().style.cursor = "";
            return;
          }

          const {
            section,
            subsection
          }: {
            id?: string;
            section?: string;
            subsection?: string;
          } = detailsPageMatch?.params || {};

          let sectionPath = "status";
          if (section) {
            sectionPath = subsection ? `${section}/${subsection}` : section;
          }

          history.push(
            `/hotspots/${selectedHotspotId}/${sectionPath}${search}`
          );

          map.getCanvas().style.cursor = "";
        }}
        circleOnMouseEnter={(e: MapMouseEvent) => {
          map.getCanvas().style.cursor = "pointer";
        }}
        circleOnMouseLeave={(e: MapMouseEvent) => {
          map.getCanvas().style.cursor = "";
        }}
      />
      <GeoJSONLayer
        id="sight-lines"
        data={
          isEmpty(lineOfSightLinesToDisplay)
            ? emptyGeoJSON
            : {
                type: "FeatureCollection",
                features: lineOfSightLinesToDisplay.map((coordinates) => {
                  const pointDistance = pointDistanceMemoized(...coordinates);
                  return {
                    type: "Feature",
                    geometry: {
                      type: "LineString",
                      coordinates
                    },
                    properties: {
                      distance: renderDistance(
                        pointDistance,
                        pointDistance > 1000
                      )
                    }
                  };
                })
              }
        }
        linePaint={{
          "line-width": 4,
          "line-opacity": 0.8,
          "line-color": lineOfSightLineColor
        }}
        lineLayout={sightLineLayout}
        symbolLayout={placementLineSymbolLayout}
        symbolPaint={{
          "text-color": isDark ? "white" : "green"
        }}
      />
      {!isEmpty(lineOfSightLinesToDisplay) && (
        <>
          {!isEditingStart && (
            <Marker
              style={{
                cursor: "pointer",
                marginTop: -10
              }}
              anchor="center"
              coordinates={lineOfSightLinesToDisplay[0][0]}
            >
              <svg width="22" height="26" cursor="pointer">
                <path
                  className="draggable"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.57538 3.07538C7.67588 -1.02513 14.3241 -1.02513 18.4246 3.07538C22.5251 7.17588 22.5251 13.8241 18.4246 17.9246L11 25.3492L3.57538 17.9246C-0.525126 13.8241 -0.525126 7.17588 3.57538 3.07538ZM11 13.5C12.6569 13.5 14 12.1569 14 10.5C14 8.84315 12.6569 7.5 11 7.5C9.34315 7.5 8 8.84315 8 10.5C8 12.1569 9.34315 13.5 11 13.5Z"
                  fill={isDark ? "#fff" : "#555555"}
                />
              </svg>
            </Marker>
          )}
          {!isEditingEnd && (
            <Marker
              style={{
                cursor: "pointer",
                marginTop: -10
              }}
              anchor="center"
              coordinates={lineOfSightLinesToDisplay[0][1]}
            >
              <svg width="22" height="26" cursor="pointer">
                <path
                  className="draggable"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.57538 3.07538C7.67588 -1.02513 14.3241 -1.02513 18.4246 3.07538C22.5251 7.17588 22.5251 13.8241 18.4246 17.9246L11 25.3492L3.57538 17.9246C-0.525126 13.8241 -0.525126 7.17588 3.57538 3.07538ZM11 13.5C12.6569 13.5 14 12.1569 14 10.5C14 8.84315 12.6569 7.5 11 7.5C9.34315 7.5 8 8.84315 8 10.5C8 12.1569 9.34315 13.5 11 13.5Z"
                  fill={"#a96ceb"}
                />
              </svg>
            </Marker>
          )}
        </>
      )}

      {visibleLayers[VisualizationLayer.cellPoints] && <CbrsLayer />}
    </>
  );
};

export default MapContent;

//
// Utils
//

export const emptyGeoJSON = (geoJSON as any).parse([], {
  Point: ["lat", "lng"]
});

const h3HighFidelityToGeoJSON = memoize((h3Index: string) => ({
  type: "Feature",
  geometry: {
    type: "MultiPolygon",
    coordinates: h3SetToMultiPolygonMemoized(
      h3ToChildrenMemoized(h3Index, 10),
      true
    )
  }
}));

const locationCirclePaint = {
  "circle-color": ["get", "color"],
  "circle-stroke-color": "rgba(156, 163, 175,0.5)",
  "circle-stroke-width": 3,
  "circle-radius": 4,
  "circle-blur": 0.1
};

const hotspotCirclePaint = {
  "circle-color": ["get", "color"],
  "circle-stroke-color": "transparent",
  "circle-stroke-width": 5,
  "circle-radius": 5,
  "circle-blur": 0.15
};

const fetchedDataBoundaryLayout = { "line-cap": "round", "line-join": "round" };

const fetchedDataBoundaryPaint = {
  "line-width": 4,
  "line-color": "#a96ceb",
  "line-opacity": 0.4
};

const currentHexRingLayout = { "line-cap": "round", "line-join": "round" };

const neighborHexRingPaint = {
  "line-width": 1,
  "line-color": "rgba(156, 163, 175, 0.5)"
};

const neighborHexLabelSymbolLayout = {
  "text-field": "{letter}",
  "text-anchor": "center"
};

const tooCloseToWitnessLinePaint = {
  "line-width": 2,
  "line-opacity": 0.5,
  "line-color": invalidColor
};

const tooCloseToWitnessLineLayout = {
  "line-cap": "round",
  "line-join": "round"
};

const installLinePaint = {
  "line-width": 2,
  "line-opacity": 0.3,
  "line-color": "rgb(156, 163, 175)"
};

const sightLineLayout = tooCloseToWitnessLineLayout;

const installLineLayout = tooCloseToWitnessLineLayout;

const placementLineSymbolLayout = {
  "symbol-placement": "line",
  "text-offset": [0, 1],
  "text-anchor": "center",
  "text-font": ["Open Sans Regular"],
  "text-field": "{distance}",
  "text-size": 16
};

const mapFeatureToItem = (f: any) => ({
  lat: f.geometry.coordinates[1],
  lng: f.geometry.coordinates[0],
  ...f.properties
});
